import type {
  MembershipNotificationsSchema,
  UnreadTimeoffsSchema
} from '@libs/data-access/queries'
import { Badge, DropdownMenu, Loader } from '@libs/ui/ds'
import { useAppNavBarTranslation } from '@libs/utils/translations'
import { routes } from '@spa/core/router'
import { useMemo } from 'react'
import type { FC } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

type NotificationLineProps = {
  count: number
  path: string
  label: string
}

const NotificationLine: FC<NotificationLineProps> = ({
  label,
  count,
  path
}) => {
  const navigate = useNavigate()
  if (!count) {
    return null
  }

  return (
    <DropdownMenu.Option
      onClick={() => navigate(path)}
      rightSection={
        <Badge
          variant='plain'
          color='semantic.red'
          size='xs'
          label={String(count)}
        />
      }
    >
      {label}
    </DropdownMenu.Option>
  )
}

type Props = {
  count: number | undefined
  notifications: MembershipNotificationsSchema | undefined
  isLoading?: boolean
}

const unreadTimeoffShape = {
  paid_leave: 0,
  unpaid_leave: 0,
  temporary_unavailability: 0,
  rtt: 0,
  family_event: 0
} as UnreadTimeoffsSchema

export const NotificationsDropdownMenuContent: FC<Props> = ({
  notifications,
  count,
  isLoading = false
}) => {
  const { t } = useAppNavBarTranslation()

  const sumUpSubProperties = useMemo(
    () =>
      !notifications?.unread_timeoffs
        ? unreadTimeoffShape
        : notifications.unread_timeoffs,
    [notifications]
  )

  if (isLoading) {
    return (
      <DropdownMenu.Dropdown className='min-h-13'>
        <Loader.Overlay />
      </DropdownMenu.Dropdown>
    )
  }

  if (!count) {
    return (
      <DropdownMenu.Dropdown>
        <DropdownMenu.Option disabled>
          {t('notifications.noRequests')}
        </DropdownMenu.Option>
      </DropdownMenu.Dropdown>
    )
  }

  return (
    <DropdownMenu.Dropdown>
      <NotificationLine
        label={t('notifications.unreadMessages')}
        count={notifications?.unread_conversations || 0}
        path={generatePath(routes.conversations.rootPath)}
      />
      <NotificationLine
        label={t('notifications.paidLeaveRequests')}
        count={sumUpSubProperties.paid_leave}
        path={generatePath(routes.timeoffs.rootPath)}
      />
      <NotificationLine
        label={t('notifications.unpaidLeaveRequests')}
        count={sumUpSubProperties.unpaid_leave}
        path={generatePath(routes.timeoffs.rootPath)}
      />
      <NotificationLine
        label={t('notifications.unavailabilityRequests')}
        count={sumUpSubProperties.temporary_unavailability}
        path={generatePath(routes.timeoffs.rootPath)}
      />
      <NotificationLine
        label={t('notifications.rttRequests')}
        count={sumUpSubProperties.rtt}
        path={generatePath(routes.timeoffs.rootPath)}
      />
      <NotificationLine
        label={t('notifications.otherLeaveRequests')}
        count={sumUpSubProperties.family_event}
        path={generatePath(routes.timeoffs.rootPath)}
      />
    </DropdownMenu.Dropdown>
  )
}
